export type CompiledAppAnalytics = {
  provider: 'ga4' | 'gtm' | 'clarity';
  key: string;
};

let activeAnalytics: CompiledAppAnalytics | null = null;

function isDevBuild(): boolean {
  return typeof import.meta !== 'undefined' && Boolean(import.meta.env?.DEV);
}

function appendScript(id: string, src?: string, inline?: string): void {
  if (document.getElementById(id)) {
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  if (src) {
    script.async = true;
    script.src = src;
  }
  if (inline) {
    script.text = inline;
  }
  document.head.appendChild(script);
}

function injectGa4(measurementId: string): void {
  if (isDevBuild() && !(window as Window & { __ip4xGa4FetchLog?: boolean }).__ip4xGa4FetchLog) {
    (window as Window & { __ip4xGa4FetchLog?: boolean }).__ip4xGa4FetchLog = true;
    const fetch = window.fetch.bind(window);
    window.fetch = (input, init) => {
      const url = String(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url);
      if (url.includes('google-analytics.com')) {
        console.debug('[ga4 outbound] fetch', url);
      }
      return fetch(input, init);
    };
  }

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer?.push(arguments);
    };
  }

  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.gtag('js', new Date());
  window.gtag('config', measurementId, isDevBuild() ? { debug_mode: true } : {});
  appendScript('ip4x-ga4', `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`);
}

function injectGtm(containerId: string): void {
  window.dataLayer = window.dataLayer || [];
  appendScript('ip4x-gtm', undefined, `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${containerId}');`);
}

function injectClarity(projectId: string): void {
  appendScript('ip4x-clarity', undefined, `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${projectId}");`);
}

export function getActiveAppAnalytics(): CompiledAppAnalytics | null {
  return activeAnalytics;
}

export function injectAppAnalytics(analytics?: CompiledAppAnalytics | null): void {
  if (!analytics?.provider || !analytics.key) {
    activeAnalytics = null;
    return;
  }

  const marker = `${analytics.provider}:${analytics.key}`;
  if (document.documentElement.dataset.ip4xAnalyticsInjected === marker) {
    activeAnalytics = analytics;
    return;
  }

  document.documentElement.dataset.ip4xAnalyticsInjected = marker;
  activeAnalytics = analytics;

  switch (analytics.provider) {
    case 'ga4':
      injectGa4(analytics.key);
      break;
    case 'gtm':
      injectGtm(analytics.key);
      break;
    case 'clarity':
      injectClarity(analytics.key);
      break;
    default:
      break;
  }

  if (isDevBuild()) {
    console.debug('[analytics] injected', analytics.provider);
  }
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
  }
}
